// Piezas compartidas por el Monitor y la pestaña Ventas de Punto V: alertas
// de máquinas sin vender, máquinas fuera de servicio, lista de máquinas y
// productos más vendidos. Solo presentación: la alerta, el "fuera de
// servicio" y todos los montos los calcula el backend.
//
// Las alertas cerradas y el tiempo de alerta se guardan en las mismas claves
// para las dos pestañas: lo que se cierra en una queda cerrado en la otra.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, Clock, ChevronDown, ChevronUp, X, Bell, BellOff, Star } from 'lucide-react';
import { fmtUsd, fmtPct, fmtEntero } from './formato';

export const VISIBLES = 5;
export const CLAVE_CLIENTES = 'puntov.monitor.clientes';
export const CLAVE_ALERTAS_OCULTAS = 'puntov.monitor.alertasOcultas';
export const MAX_ALERTAS_OCULTAS = 300;
export const COLOR_VENTA = '#2a78d6';
export const MINUTOS_DIA = 24 * 60;

export const leer = (clave, porDefecto) => {
  try {
    const v = JSON.parse(localStorage.getItem(clave));
    return v ?? porDefecto;
  } catch {
    return porDefecto;
  }
};
export const guardar = (clave, valor) => {
  try { localStorage.setItem(clave, JSON.stringify(valor)); } catch { /* sin almacenamiento local */ }
};

// "45 min", "3 h 20 min", "9 d 7 h".
export const fmtDuracion = (min) => {
  if (min < 60) return `${min} min`;
  if (min < MINUTOS_DIA) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  const d = Math.floor(min / MINUTOS_DIA);
  const h = Math.floor((min % MINUTOS_DIA) / 60);
  return h ? `${d} d ${h} h` : `${d} d`;
};

export const fmtMomento = (isoConZona) => new Date(isoConZona).toLocaleString('es-VE', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
});

// Una alerta se identifica solo por cliente y máquina: una máquina nunca
// cuenta dos veces. Si ya se cerró, sigue cerrada aunque cambie su tiempo
// sin vender (el tiempo se actualiza, no se suma como nueva).
export const firmaAlerta = (base, m) => `${base}|${m.id_maquina}`;

// Las alertas cerradas antes se guardaban como "base|máquina|última venta":
// se dejan en "base|máquina" sin duplicados.
export const leerAlertasCerradas = () => new Set(
  (Array.isArray(leer(CLAVE_ALERTAS_OCULTAS, [])) ? leer(CLAVE_ALERTAS_OCULTAS, []) : [])
    .map(f => String(f).split('|').slice(0, 2).join('|'))
);

// Opciones del tiempo sin vender para alertar (minutos). Por defecto 2 h.
export const UMBRALES_ALERTA = [30, 60, 120, 180, 240, 360, 720];
export const UMBRAL_POR_DEFECTO = 120;
export const CLAVE_UMBRAL = 'puntov.monitor.alertaMin';

// Estado visual de una máquina. La alerta y el "fuera de servicio" los
// decide el backend; pasar de un día sin vender solo pinta la alerta en rojo.
export const estadoMaquina = (m) => {
  if (!m.activa) return { punto: 'bg-gray-300', borde: 'border-l-gray-300', texto: 'text-gray-400', etiqueta: 'Inactiva', orden: 3 };
  if (m.fuera_de_servicio) return { punto: 'bg-gray-400', borde: 'border-l-gray-400', texto: 'text-gray-500', etiqueta: 'Fuera de servicio', orden: 2 };
  if (!m.alerta_sin_venta) return { punto: 'bg-green-500', borde: 'border-l-green-500', texto: 'text-gray-500', etiqueta: 'Vendiendo', orden: 1 };
  if (m.minutos_sin_venta >= MINUTOS_DIA) return { punto: 'bg-red-500', borde: 'border-l-red-500', texto: 'text-red-700', etiqueta: 'Sin vender', orden: 1 };
  return { punto: 'bg-amber-500', borde: 'border-l-amber-500', texto: 'text-amber-700', etiqueta: 'Sin vender', orden: 1 };
};

// Nombre que se muestra: la descripción de Punto V; el código va aparte.
export const NombreMaquina = ({ m }) => (
  <span className="min-w-0 truncate">
    <span className="text-gray-800 font-medium">{m.descripcion || m.id_maquina}</span>
    {m.descripcion && <span className="ml-1.5 font-mono text-[10px] text-gray-400">{m.id_maquina}</span>}
  </span>
);

export const textoUltimaVenta = (m) => {
  if (m.minutos_sin_venta == null) return 'Nunca ha vendido · fuera de servicio';
  const texto = `Última venta hace ${fmtDuracion(m.minutos_sin_venta)}`;
  return m.fuera_de_servicio ? `${texto} · fuera de servicio` : texto;
};

// Lista que arranca recogida en los primeros VISIBLES elementos.
export const usePlegable = (items) => {
  const [abierta, setAbierta] = useState(false);
  const visibles = abierta ? items : items.slice(0, VISIBLES);
  const boton = items.length > VISIBLES ? (
    <button
      onClick={() => setAbierta(v => !v)}
      className="mt-2 w-full h-8 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded-lg flex items-center justify-center gap-1"
    >
      {abierta ? <>Ver menos <ChevronUp size={14} /></> : <>Ver {items.length - VISIBLES} más <ChevronDown size={14} /></>}
    </button>
  ) : null;
  return [visibles, boton];
};

export const AlertasSinVenta = ({ maquinas, umbral, onCerrar }) => {
  const [visibles, boton] = usePlegable(maquinas);
  if (!maquinas.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
      <div className="flex items-start gap-1.5 text-xs font-semibold text-amber-800">
        <AlertTriangle size={14} className="mt-px flex-shrink-0" />
        <span className="flex-1">
          {maquinas.length === 1 ? '1 máquina' : `${maquinas.length} máquinas`} sin vender
          {umbral ? ` hace más de ${fmtDuracion(umbral)}` : ''}
        </span>
        <button
          onClick={onCerrar}
          title="Cerrar alerta (vuelve a salir si otra máquina deja de vender)"
          aria-label="Cerrar alerta"
          className="-mr-1 -mt-0.5 p-0.5 rounded text-amber-700 hover:bg-amber-100"
        >
          <X size={15} />
        </button>
      </div>
      <ul className="mt-2 space-y-1.5">
        {visibles.map(m => {
          const e = estadoMaquina(m);
          return (
            <li key={m.id_maquina} className="text-xs">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${e.punto}`} />
                <NombreMaquina m={m} />
              </div>
              <div className={`ml-3.5 tabular-nums ${e.texto}`}>
                Sin vender hace {fmtDuracion(m.minutos_sin_venta)} · desde {fmtMomento(m.ultima_venta)}
              </div>
            </li>
          );
        })}
      </ul>
      {boton}
    </div>
  );
};

// Campana con las máquinas fuera de servicio (más de N días sin vender o
// nunca han vendido) de los clientes en pantalla, agrupadas por cliente.
export const AvisosFueraDeServicio = ({ grupos, dias }) => {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef(null);
  const total = grupos.reduce((acc, g) => acc + g.maquinas.length, 0);

  useEffect(() => {
    if (!abierto) return undefined;
    const alClicFuera = (e) => { if (!contenedor.current?.contains(e.target)) setAbierto(false); };
    const alEscape = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', alClicFuera);
    document.addEventListener('keydown', alEscape);
    return () => {
      document.removeEventListener('mousedown', alClicFuera);
      document.removeEventListener('keydown', alEscape);
    };
  }, [abierto]);

  return (
    <div className="relative" ref={contenedor}>
      <button
        onClick={() => setAbierto(v => !v)}
        aria-expanded={abierto}
        aria-label={`Máquinas fuera de servicio: ${total}`}
        title="Máquinas fuera de servicio"
        className="relative h-9 w-9 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center justify-center"
      >
        <Bell size={16} />
        {total > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center tabular-nums">
            {total}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg z-20">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-800">Máquinas fuera de servicio</div>
            <div className="text-[11px] text-gray-500">
              Activas en Punto V, pero {dias ? `con más de ${dias} días` : 'con mucho tiempo'} sin vender o que nunca han vendido.
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto p-2">
            {total === 0 && <p className="px-2 py-6 text-center text-xs text-gray-500">Ninguna máquina fuera de servicio en los clientes que está viendo.</p>}
            {grupos.filter(g => g.maquinas.length).map(g => (
              <div key={g.base} className="mb-2 last:mb-0">
                <div className="px-2 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{g.nombre}</div>
                <ul>
                  {g.maquinas.map(m => (
                    <li key={m.id_maquina} className="px-2 py-1.5 rounded-lg hover:bg-gray-50 text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-400" />
                        <NombreMaquina m={m} />
                      </div>
                      <div className="ml-3.5 text-gray-500 tabular-nums">
                        {m.minutos_sin_venta == null
                          ? 'Nunca ha vendido'
                          : `Sin vender hace ${fmtDuracion(m.minutos_sin_venta)} · desde ${fmtMomento(m.ultima_venta)}`}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const nombreProducto = (p) => p.producto || `Producto #${p.id_producto}`;
export const detalleProducto = (p) => [p.marca, p.peso_neto].filter(Boolean).join(' · ');

// Ranking de productos por unidades vendidas. `compacto` es la versión que
// va dentro de una máquina abierta.
export const RankingProductos = ({ productos, compacto = false, inicio = 0 }) => {
  const maximo = Math.max(0, ...productos.map(p => p.unidades));
  return (
    <ol className={compacto ? 'space-y-1.5' : 'space-y-2.5'}>
      {productos.map((p, i) => (
        <li key={p.id_producto} className="flex items-start gap-2 text-xs">
          <span className={`flex-shrink-0 rounded-full flex items-center justify-center font-semibold tabular-nums ${
            compacto ? 'w-4 h-4 text-[10px] text-gray-500' : `w-5 h-5 text-[11px] ${inicio + i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`
          }`}
          >
            {inicio + i + 1}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-gray-800 truncate" title={nombreProducto(p)}>{nombreProducto(p)}</span>
              <span className="tabular-nums font-medium text-gray-900 whitespace-nowrap">{fmtEntero(p.unidades)} u</span>
            </div>
            {!compacto && (
              <>
                <div className="flex items-baseline justify-between gap-2 text-[11px] text-gray-500">
                  <span className="truncate">{detalleProducto(p) || ' '}</span>
                  <span className="tabular-nums whitespace-nowrap">{fmtUsd(p.venta)}</span>
                </div>
                <div className="mt-1 h-1 bg-gray-100 rounded">
                  {maximo > 0 && <div className="h-1 rounded bg-blue-300" style={{ width: `${(p.unidades / maximo) * 100}%` }} />}
                </div>
              </>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export const ProductosDelCliente = ({ productos }) => {
  const [visibles, boton] = usePlegable(productos || []);
  if (!productos?.length) return null;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs font-medium text-gray-600">Productos más vendidos</div>
        <div className="text-[11px] text-gray-400">Unidades del periodo</div>
      </div>
      <RankingProductos productos={visibles} />
      {boton}
    </div>
  );
};

// Todas las máquinas del cliente: activas por venta del periodo, inactivas
// al final. Al tocar una máquina se abren sus productos más vendidos.
export const ListaMaquinas = ({ maquinas }) => {
  const [abierta, setAbierta] = useState(null);
  const ordenadas = useMemo(
    () => [...maquinas].sort((a, b) => (estadoMaquina(a).orden - estadoMaquina(b).orden) || (b.venta - a.venta)),
    [maquinas]
  );
  const [visibles, boton] = usePlegable(ordenadas);
  const ventaMaxima = Math.max(0, ...ordenadas.map(m => m.venta));

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs font-medium text-gray-600">Máquinas</div>
        <div className="text-[11px] text-gray-400">Venta del periodo</div>
      </div>
      {/* Cada máquina es una tarjeta; la franja izquierda lleva el color de su estado. */}
      <ul className="space-y-2">
        {visibles.map(m => {
          const e = estadoMaquina(m);
          const tieneProductos = m.productos?.length > 0;
          const estaAbierta = abierta === m.id_maquina;
          return (
            <li
              key={m.id_maquina}
              title={e.etiqueta}
              className={`rounded-lg border border-gray-200 border-l-4 ${e.borde} bg-white px-3 py-2.5 text-xs ${m.activa ? '' : 'opacity-60'}`}
            >
              <button
                type="button"
                onClick={() => tieneProductos && setAbierta(estaAbierta ? null : m.id_maquina)}
                disabled={!tieneProductos}
                aria-expanded={estaAbierta}
                title={tieneProductos ? 'Ver productos más vendidos' : e.etiqueta}
                className={`w-full flex items-start justify-between gap-2 text-left ${tieneProductos ? 'hover:text-blue-700' : 'cursor-default'}`}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1 text-sm">
                    <span className="font-semibold text-gray-900 truncate">{m.descripcion || m.id_maquina}</span>
                    {tieneProductos && (estaAbierta
                      ? <ChevronUp size={14} className="text-gray-400 flex-shrink-0" />
                      : <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />)}
                  </span>
                  {m.descripcion && <span className="block font-mono text-[10px] text-gray-400">{m.id_maquina}</span>}
                </span>
                <span className="text-right whitespace-nowrap">
                  <span className="block text-sm tabular-nums font-semibold text-gray-900">{fmtUsd(m.venta)}</span>
                  <span className="block text-[11px] tabular-nums text-green-700" title={`Margen ${fmtPct(m.margen_pct)}`}>
                    Ganancia {fmtUsd(m.ganancia)}
                  </span>
                </span>
              </button>
              <div className="mt-1.5 h-1.5 bg-gray-100 rounded">
                {ventaMaxima > 0 && <div className="h-1.5 rounded" style={{ width: `${(m.venta / ventaMaxima) * 100}%`, background: COLOR_VENTA }} />}
              </div>
              <div className={`mt-1 flex items-center gap-1 text-[11px] whitespace-nowrap min-w-0 ${e.texto}`}>
                {m.activa ? <><Clock size={11} className="flex-shrink-0" /> <span className="truncate">{textoUltimaVenta(m)}</span></> : 'Inactiva'}
                {m.activa && m.n_ventas > 0 && <span className="text-gray-400"> · {fmtEntero(m.n_ventas)} ventas</span>}
              </div>
              {!tieneProductos && (
                <div className="mt-2 rounded-md bg-gray-50 px-2 py-1 text-[11px] text-gray-400">Sin ventas en el periodo</div>
              )}
              {tieneProductos && !estaAbierta && (
                <div className="mt-2 flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] min-w-0" title={detalleProducto(m.productos[0]) || undefined}>
                  <Star size={11} className="flex-shrink-0 text-amber-500 fill-amber-400" />
                  <span className="font-medium text-gray-800 truncate">{nombreProducto(m.productos[0])}</span>
                  <span className="ml-auto pl-1 text-gray-500 tabular-nums whitespace-nowrap">{fmtEntero(m.productos[0].unidades)} u</span>
                </div>
              )}
              {estaAbierta && (
                <div className="mt-2 rounded-md bg-gray-50 px-2 py-2">
                  <div className="text-[11px] text-gray-500 mb-1">Más vendidos en esta máquina</div>
                  <RankingProductos productos={m.productos} compacto />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {boton}
    </div>
  );
};

// Alertas cerradas por el usuario (firmas "base|máquina"), guardadas en este
// navegador.
export const useAlertasCerradas = () => {
  const [ocultas, setOcultas] = useState(leerAlertasCerradas);

  const actualizar = (cambio) => setOcultas(prev => {
    const siguiente = cambio(new Set(prev));
    guardar(CLAVE_ALERTAS_OCULTAS, [...siguiente].slice(-MAX_ALERTAS_OCULTAS));
    return siguiente;
  });

  const ocultar = (firmas) => actualizar(s => { firmas.forEach(f => s.add(f)); return s; });
  const mostrar = (firmas) => actualizar(s => { firmas.forEach(f => s.delete(f)); return s; });

  return { ocultas, ocultar, mostrar };
};

// Tiempo sin vender para alertar, elegido por el usuario (minutos).
export const useUmbralAlerta = () => {
  const [umbral, setUmbral] = useState(() => {
    const guardado = Number(leer(CLAVE_UMBRAL, UMBRAL_POR_DEFECTO));
    return UMBRALES_ALERTA.includes(guardado) ? guardado : UMBRAL_POR_DEFECTO;
  });
  const elegir = (valor) => {
    setUmbral(valor);
    guardar(CLAVE_UMBRAL, valor);
  };
  return [umbral, elegir];
};

export const SelectorUmbral = ({ umbral, onCambiar, className }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">Alertar sin ventas después de</span>
    <select value={umbral} onChange={(e) => onCambiar(Number(e.target.value))} className={className}>
      {UMBRALES_ALERTA.map(min => <option key={min} value={min}>{fmtDuracion(min)}</option>)}
    </select>
  </label>
);

// Máquinas en alerta de un cliente, de la que lleva más a la que lleva menos
// tiempo sin vender.
export const maquinasEnAlerta = (datos) => (datos ? datos.por_maquina
  .filter(m => m.alerta_sin_venta)
  .sort((a, b) => (b.minutos_sin_venta ?? Infinity) - (a.minutos_sin_venta ?? Infinity)) : []);

// Máquinas fuera de servicio: nunca vendió primero; luego de más a menos
// tiempo sin vender.
export const maquinasFueraDeServicio = (datos) => (datos?.por_maquina || [])
  .filter(m => m.fuera_de_servicio)
  .sort((x, y) => (y.minutos_sin_venta ?? Infinity) - (x.minutos_sin_venta ?? Infinity));

// Alertas de un cliente. Si el usuario ya cerró alguna, todo queda recogido
// en una barra: las que aparecen después se suman con un punto rojo en lugar
// de abrir el recuadro encima.
export const BloqueAlertas = ({ base, datos, ocultas, onOcultar, onMostrar }) => {
  const enAlerta = useMemo(() => maquinasEnAlerta(datos), [datos]);
  const visibles = enAlerta.filter(m => !ocultas.has(firmaAlerta(base, m)));
  const cerradas = enAlerta.length - visibles.length;

  if (!datos || !enAlerta.length) return null;

  if (cerradas > 0) {
    return (
      <button
        onClick={() => onMostrar(enAlerta.map(m => firmaAlerta(base, m)))}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 px-3 py-2 text-xs text-amber-800 hover:bg-amber-50"
      >
        <span className="flex items-center gap-2">
          <BellOff size={14} />
          {enAlerta.length === 1 ? '1 alerta' : `${enAlerta.length} alertas`}
          {visibles.length > 0 && (
            <span className="flex items-center gap-1 font-semibold text-red-700">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-red-400 opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-red-600" />
              </span>
              {visibles.length === 1 ? '1 nueva' : `${visibles.length} nuevas`}
            </span>
          )}
        </span>
        <span className="font-medium">Ver alertas</span>
      </button>
    );
  }

  return (
    <AlertasSinVenta
      maquinas={visibles}
      umbral={datos.minutos_alerta_sin_venta}
      onCerrar={() => onOcultar(visibles.map(m => firmaAlerta(base, m)))}
    />
  );
};
